import { describeDomains, noArgsSchema } from "../operation-meta.js";
import { assertCompanyWriteAccess, assertPersonAdminFieldWriteAccess, assertPersonWriteAccess, assertProjectCreateAccess, assertProjectGroupWriteAccess, assertProjectWriteAccess, normalizeProjectInput, queueClockifyProjectGroupSync, queueClockifyProjectSync, } from "../projects-helpers.js";
import { companyIDInputSchema, createCompanyInputSchema, createPersonInputSchema, createProjectGroupInputSchema, listPeopleQueryArgsSchema, personByUserIDQueryArgsSchema, createProjectInputSchema, personIDInputSchema, personByIDQueryArgsSchema, projectGroupIDInputSchema, projectIDInputSchema, updateCompanyInputSchema, updatePersonInputSchema, updateProjectGroupInputSchema, updateProjectInputSchema, idQueryArgsSchema, includeDeletedQueryArgsSchema, myUpcomingProjectsQueryArgsSchema, projectsByIDsQueryArgsSchema, } from "../projects-input-schemas.js";
function authorizedProjectRowsQuery(projectZql, ctx) {
    const personID = ctx.personID;
    return ctx.role === "admin" || ctx.role === "employee"
        ? projectZql.projects
        : ctx.role === "freelancer" && personID
            ? projectZql.projects.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID))
            : projectZql.projects.where("id", "__unauthorized__");
}
function authorizedProjectsQuery(projectZql, ctx) {
    return authorizedProjectRowsQuery(projectZql, ctx)
        .related("companies")
        .related("contact")
        .related("project_manager")
        .related("account_manager")
        .related("technical_responsible")
        .related("equipment_responsible")
        .related("project_groups");
}
function authorizedCompaniesQuery(projectZql, ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.companies.where("id", "__unauthorized__");
    }
    return projectZql.companies;
}
function withCompanyDeletedScope(query, includeDeleted) {
    return includeDeleted ? query : query.where("deletedAt", "IS", null);
}
function authorizedProjectGroupsQuery(projectZql, ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.projectGroups.where("id", "__unauthorized__");
    }
    return projectZql.projectGroups;
}
function projectGroupsWithRelations(projectZql, ctx) {
    return authorizedProjectGroupsQuery(projectZql, ctx)
        .related("companies")
        .related("contact")
        .related("projects");
}
function withProjectGroupDeletedScope(query, includeDeleted) {
    return includeDeleted ? query : query.where("deletedAt", "IS", null);
}
function authorizedPeopleQuery(projectZql, ctx) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.people;
    }
    return ctx.userID
        ? projectZql.people.where("userID", ctx.userID)
        : projectZql.people.where("id", "__unauthorized__");
}
function peopleWithCompany(projectZql, ctx) {
    return authorizedPeopleQuery(projectZql, ctx)
        .related("companies")
        .related("personAllergyTags", (assignment) => assignment.related("allergyTag"));
}
function withPeopleDeletedScope(query, includeDeleted) {
    return includeDeleted ? query : query.where("deletedAt", "IS", null);
}
export function createCoreQueries({ defineProjectQuery, projectZql, }) {
    if (!defineProjectQuery) {
        throw new Error("Missing core query dependencies");
    }
    return {
        projects: {
            commandItems: defineProjectQuery(noArgsSchema, ({ ctx }) => authorizedProjectRowsQuery(projectZql, ctx)
                .where("deletedAt", "IS", null)
                .orderBy("eventStartDate", "desc")
                .orderBy("createdAt", "desc")),
            list: defineProjectQuery({
                input: noArgsSchema,
                description: "All projects visible to the caller (including soft-deleted ones; check deletedAt), latest event first, with customer companies, contact, project/account manager, technical and equipment responsible and project groups. Admins and employees see every project, freelancers only projects they collaborate on. Dates are epoch ms.",
            }, ({ ctx }) => authorizedProjectsQuery(projectZql, ctx)
                .orderBy("eventStartDate", "desc")
                .orderBy("createdAt", "desc")),
            byIDs: defineProjectQuery({
                input: projectsByIDsQueryArgsSchema,
                description: "The given non-deleted projects the caller can see, with companies, contact, responsible people and project groups. Unknown or inaccessible IDs are left out; the order is not meaningful. Dates are epoch ms.",
            }, ({ args, ctx }) => authorizedProjectsQuery(projectZql, ctx)
                .where("id", "IN", args.ids)
                .where("deletedAt", "IS", null)),
            myUpcoming: defineProjectQuery({
                input: myUpcomingProjectsQueryArgsSchema,
                description: "The caller's own project availability entries with status requested, available or booked, oldest first, each with its non-deleted project (companies, contact, responsible people, project groups). Returns nothing unless personID is the caller's own person ID. Dates are epoch ms.",
            }, ({ args, ctx, }) => {
                if (!ctx.personID || ctx.personID !== args.personID) {
                    return projectZql.projectAvailability.where("id", "__unauthorized__");
                }
                return projectZql.projectAvailability
                    .where("personID", args.personID)
                    .where("status", "IN", ["requested", "available", "booked"])
                    .related("project", (project) => project
                    .where("deletedAt", "IS", null)
                    .related("companies")
                    .related("contact")
                    .related("project_manager")
                    .related("account_manager")
                    .related("technical_responsible")
                    .related("equipment_responsible")
                    .related("project_groups"))
                    .orderBy("createdAt", "asc");
            }),
        },
        companies: {
            list: defineProjectQuery({
                input: includeDeletedQueryArgsSchema,
                description: "Companies (customers, suppliers, ...) sorted by name. Admins and employees only; soft-deleted companies are excluded unless includeDeleted is set.",
            }, ({ args, ctx, }) => withCompanyDeletedScope(authorizedCompaniesQuery(projectZql, ctx), args.includeDeleted).orderBy("name", "asc")),
            byID: defineProjectQuery({
                input: idQueryArgsSchema,
                description: "One company by ID, or null. Admins and employees only.",
            }, ({ args, ctx }) => authorizedCompaniesQuery(projectZql, ctx).where("id", args.id).one()),
        },
        projectGroups: {
            commandItems: defineProjectQuery(noArgsSchema, ({ ctx }) => withProjectGroupDeletedScope(authorizedProjectGroupsQuery(projectZql, ctx), false).orderBy("createdAt", "desc")),
            list: defineProjectQuery({
                input: includeDeletedQueryArgsSchema,
                description: "Project groups, newest first, with companies, contact and their projects. Admins and employees only; soft-deleted groups are excluded unless includeDeleted is set. Dates are epoch ms.",
            }, ({ args, ctx, }) => withProjectGroupDeletedScope(projectGroupsWithRelations(projectZql, ctx), args.includeDeleted).orderBy("createdAt", "desc")),
            byID: defineProjectQuery(idQueryArgsSchema, ({ args, ctx }) => projectGroupsWithRelations(projectZql, ctx)
                .where("id", args.id)
                .one()),
        },
        people: {
            list: defineProjectQuery({
                input: listPeopleQueryArgsSchema,
                description: "People (contacts, employees, freelancers) sorted by first and last name, with their companies and allergy tags. Admins and employees see everyone, other users only their own person record. Soft-deleted people are excluded unless includeDeleted is set.",
            }, ({ args, ctx, }) => {
                let query = withPeopleDeletedScope(peopleWithCompany(projectZql, ctx), args.includeDeleted);
                if (args.personTypes?.length === 1) {
                    query = query.where("personType", args.personTypes[0]);
                }
                else if (args.personTypes && args.personTypes.length > 1) {
                    query = query.where("personType", "IN", args.personTypes);
                }
                return query.orderBy("firstName", "asc").orderBy("lastName", "asc");
            }),
            byID: defineProjectQuery({
                input: personByIDQueryArgsSchema,
                description: "One person by ID, or null, with companies and allergy tags. Admins and employees can read anyone, other users only their own person record.",
            }, ({ args, ctx }) => peopleWithCompany(projectZql, ctx).where("id", args.id).one()),
            byUserID: defineProjectQuery({
                input: personByUserIDQueryArgsSchema,
                description: "The non-deleted person record linked to an auth user, or null. Only resolves for the caller's own user ID.",
            }, ({ args, ctx }) => {
                if (ctx.userID !== args.userID) {
                    return projectZql.people.where("id", "__unauthorized__").one();
                }
                return projectZql.people
                    .where("userID", args.userID)
                    .where("deletedAt", "IS", null)
                    .one();
            }),
        },
    };
}
export function createCoreMutators({ defineProjectMutator }) {
    if (!defineProjectMutator) {
        throw new Error("Missing core mutator dependencies");
    }
    return {
        projects: {
            create: defineProjectMutator({
                input: createProjectInputSchema,
                description: "Create a project (production/job). Pass a client-generated `id`; dates are `YYYY-MM-DD`. Status defaults to `initial_contact`. Staff only.",
            }, async ({ tx, ctx, args, }) => {
                assertProjectCreateAccess(ctx);
                await tx.mutate.projects.insert({
                    ...normalizeProjectInput(args),
                    status: args.status ?? "initial_contact",
                    types: args.types ?? [],
                    language: args.language ?? "de",
                    defaultVatRate: args.defaultVatRate ?? "CH_8_1",
                    defaultDiscountPercent: args.defaultDiscountPercent ?? 0,
                });
                queueClockifyProjectSync(ctx, args.id);
                if (args.projectGroupID) {
                    queueClockifyProjectGroupSync(ctx, args.projectGroupID);
                }
            }),
            update: defineProjectMutator({
                input: updateProjectInputSchema,
                description: "Update fields of a project. Send `id` plus only the fields to change; dates are `YYYY-MM-DD`.",
            }, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.id);
                await tx.mutate.projects.update(normalizeProjectInput(args));
                queueClockifyProjectSync(ctx, args.id);
                if (args.projectGroupID) {
                    queueClockifyProjectGroupSync(ctx, args.projectGroupID);
                }
            }),
            softDelete: defineProjectMutator({
                input: projectIDInputSchema,
                description: "Soft-delete a project (sets `deletedAt`; undo with `projects.restore`).",
            }, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.id);
                await tx.mutate.projects.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            restore: defineProjectMutator(projectIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.id);
                await tx.mutate.projects.update({
                    id: args.id,
                    deletedAt: null,
                });
            }),
        },
        companies: {
            create: defineProjectMutator({
                input: createCompanyInputSchema,
                description: "Create a company (customer, supplier, partner). Pass a client-generated `id`. Staff only.",
            }, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.insert({
                    ...args,
                    types: args.types ?? [],
                    isMainCompany: args.isMainCompany ?? false,
                });
            }),
            update: defineProjectMutator({
                input: updateCompanyInputSchema,
                description: "Update fields of a company. Send `id` plus only the fields to change.",
            }, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.update(args);
            }),
            softDelete: defineProjectMutator(companyIDInputSchema, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            restore: defineProjectMutator(companyIDInputSchema, async ({ tx, ctx, args, }) => {
                assertCompanyWriteAccess(ctx);
                await tx.mutate.companies.update({
                    id: args.id,
                    deletedAt: null,
                });
            }),
        },
        projectGroups: {
            create: defineProjectMutator(createProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.insert({
                    ...args,
                    status: args.status ?? "active",
                });
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
            update: defineProjectMutator(updateProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.update(args);
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
            softDelete: defineProjectMutator(projectGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
            restore: defineProjectMutator(projectGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectGroupWriteAccess(ctx);
                await tx.mutate.projectGroups.update({
                    id: args.id,
                    deletedAt: null,
                });
                queueClockifyProjectGroupSync(ctx, args.id);
            }),
        },
        people: {
            create: defineProjectMutator({
                input: createPersonInputSchema,
                description: "Create a person (employee, freelancer or contact). Pass a client-generated `id`. Staff only.",
            }, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                if (args.isAdmin !== undefined) {
                    assertPersonAdminFieldWriteAccess(ctx);
                }
                await tx.mutate.people.insert({
                    ...args,
                    personType: args.personType ?? "contact",
                    isAdmin: args.isAdmin ?? false,
                });
            }),
            update: defineProjectMutator({
                input: updatePersonInputSchema,
                description: "Update fields of a person. Send `id` plus only the fields to change. Only admins can change admin status.",
            }, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                if (args.isAdmin !== undefined) {
                    assertPersonAdminFieldWriteAccess(ctx);
                }
                await tx.mutate.people.update(args);
            }),
            softDelete: defineProjectMutator(personIDInputSchema, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                await tx.mutate.people.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            restore: defineProjectMutator(personIDInputSchema, async ({ tx, ctx, args, }) => {
                assertPersonWriteAccess(ctx);
                await tx.mutate.people.update({
                    id: args.id,
                    deletedAt: null,
                });
            }),
        },
    };
}
export const coreDomainDescriptions = describeDomains({
    projects: "Productions/jobs: dates, customer, status, responsible people",
    companies: "Customers, suppliers and partner companies",
    projectGroups: "Groups of related projects (e.g. a series or season)",
    people: "Employees, freelancers and contacts (crew and staff records)",
});
